import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? '/api' : '/api');

// Get CSRF token from cookie
function getCsrfToken() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add CSRF token and Accept-Language to all requests
apiClient.interceptors.request.use(async (config) => {
  // Add Accept-Language header based on current i18n language
  const currentLanguage = localStorage.getItem('i18nextLng') || 'fr';
  config.headers['Accept-Language'] = currentLanguage;
  
  // For non-GET requests, ensure we have a CSRF token
  if (config.method !== 'get') {
    let csrfToken = getCsrfToken();
    if (!csrfToken) {
      try {
        await axios.get(`${API_URL}/auth/csrf/`, { withCredentials: true });
        csrfToken = getCsrfToken();
      } catch (err) {
        console.error('Failed to fetch CSRF token:', err);
      }
    }
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
  }
  return config;
});

const authService = {
  initCsrfToken: async () => {
    try {
      await apiClient.get('/auth/csrf/');
    } catch (err) {
      console.error('Failed to get CSRF token:', err);
    }
  },

  login: async (email, password) => {
    await authService.initCsrfToken();
    const response = await apiClient.post('/auth/login/', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout/');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me/');
    return response.data;
  },

  updateProfile: async (payload) => {
    await authService.initCsrfToken();
    const response = await apiClient.patch('/auth/update_profile/', payload);
    return response.data;
  },

  changePassword: async (old_password, new_password, new_password_confirm) => {
    await authService.initCsrfToken();
    const response = await apiClient.post('/auth/change_password/', {
      old_password,
      new_password,
      new_password_confirm,
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    await authService.initCsrfToken();
    const response = await apiClient.post('/auth/password_reset/', { email });
    return response.data;
  },

  confirmPasswordReset: async (uid, token, new_password) => {
    await authService.initCsrfToken();
    const response = await apiClient.post('/auth/password_reset_confirm/', {
      uid,
      token,
      new_password,
    });
    return response.data;
  },
};

export default authService;
export { apiClient };
